import clsx from "clsx";
import {
  memo,
  type RefObject,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { HiOutlineDotsVertical } from "react-icons/hi";
import { MdBlock } from "react-icons/md";
import noImage from "@/assets/img/no-image.svg";
import { BOARD_CONFIG } from "@/config/browserBoard";
import type { Thread } from "@/entities/thread";
import {
  getImageUrl,
  getThreadBodyText,
  getThreadTitle,
  isR18ThreadTagName,
  isThreadExpired,
} from "@/entities/thread";
import { ThreadContextMenu } from "@/features/catalog/components/actions/ThreadContextMenu";
import { CatalogBoardIcon } from "@/features/catalog/components/CatalogBoardIcon";
import { CatalogReplyCount } from "@/features/catalog/components/lists/CatalogReplyCount";
import { CatalogThreadLink } from "@/features/catalog/components/navigation/CatalogThreadLink";
import { getThreadLinkAttributes } from "@/features/catalog/hooks/useFilteredThreads";
import { useCatalogStore } from "@/features/catalog/stores/catalogStore";
import {
  getCatalogImageMaskKind,
  getCatalogMaskVisibility,
  getCatalogThreadClickAction,
} from "@/features/catalog/utils/catalogReveal";
import { getCatalogCategoryTags } from "@/features/catalog/utils/catalogTags";
import { useFavStore } from "@/features/fav-filter/stores/favStore";
import {
  unreadReplyCount,
  useHistoryStore,
  useViewedHistoryItem,
} from "@/features/history/stores/historyStore";
import { useNgStore } from "@/features/ng-filter/stores";
import { useSettingsStore } from "@/features/settings/hooks";
import { useLongPress } from "@/shared/hooks";
import { decorateTitle, resolveThumbnailBadge } from "@/shared/lib";
import { useAimogeBeforeRender, useAimogeRendered } from "@/shared/lib/aimoge";
import { OekakiBadge, VideoBadge } from "@/shared/ui/media";
import { TagBadges, TextEffects } from "@/shared/ui/navigation";

interface CatalogItemProps {
  thread: Thread;
  isNew: boolean;
}

export const CatalogItem: React.FunctionComponent<CatalogItemProps> = memo(
  function CatalogItem(props: CatalogItemProps) {
    const renderedThread = useAimogeBeforeRender(
      "catalog:beforeRender",
      props.thread,
    );
    const elementRef = useAimogeRendered<HTMLElement>(
      "catalog",
      renderedThread,
      renderedThread?.id,
    );

    if (!renderedThread) return null;
    return (
      <CatalogItemContent
        thread={renderedThread}
        isNew={props.isNew}
        elementRef={elementRef}
      />
    );
  },
);

interface CatalogItemContentProps extends CatalogItemProps {
  elementRef: RefObject<HTMLElement | null>;
}

const CatalogItemContent: React.FunctionComponent<CatalogItemContentProps> =
  memo(function CatalogItemContent({
    thread,
    isNew,
    elementRef,
  }: CatalogItemContentProps) {
    const {
      textLines,
      imageSize,
      openInNewTab,
      showNew,
      showCount,
      showUnreadCount,
      catalogAnim,
      threadMenuOpenMethod,
    } = useCatalogStore();
    const showR18 = useSettingsStore((state) => state.showR18);
    const extendedTextEffects = useSettingsStore(
      (state) => state.extendedTextEffects,
    );
    const boardSlug = thread.b ?? BOARD_CONFIG.boardKey;
    const addViewed = useHistoryStore((state) => state.addViewed);
    const viewedItem = useViewedHistoryItem(boardSlug, thread.id);
    const isSuperFavorite = useFavStore(
      (state) => state.isThreadFavorite(thread) === 2,
    );
    const { isThreadHidden, showNgContent } = useNgStore();
    const [menuOpen, setMenuOpen] = useState(false);
    const [ngRevealed, setNgRevealed] = useState(false);
    const [r18Revealed, setR18Revealed] = useState(false);
    const [isHovered, setIsHovered] = useState(false);
    const thumbnailLinkRef = useRef<HTMLAnchorElement>(null);
    const r18TriggerRef = useRef<HTMLButtonElement>(null);
    const pendingFocusRef = useRef<"thumbnail" | "r18" | null>(null);

    // カタログでのアニメ画像が許可されている場合のみアニメ画像のみ動かし、それ以外の場合は動かさない
    const imageUrl = getImageUrl(
      thread.opPost.attachment,
      (catalogAnim === "always" || (catalogAnim === "hover" && isHovered)) &&
        thread.opPost.attachment?.kind === "animated",
    );
    const accessibleTitle = decorateTitle(getThreadTitle(thread));
    const displayBody = decorateTitle(getThreadBodyText(thread));
    const categoryTags = getCatalogCategoryTags(thread.tags);
    const totalCount = thread.replyCount;
    const thumbnailBadge = resolveThumbnailBadge(thread.opPost.attachment);
    const threadLinkAttributes = getThreadLinkAttributes(openInNewTab);
    const isVideo = thumbnailBadge === "video";
    const isOekaki = thumbnailBadge === "oekaki";
    const isNg = showNgContent && isThreadHidden(thread);
    const isR18 = thread.tags.some((tag) => isR18ThreadTagName(tag.name));
    const isR18Hidden =
      isR18 && Boolean(thread.opPost.attachment) && !showR18 && !r18Revealed;
    const maskVisibility = getCatalogMaskVisibility({
      isNg,
      ngRevealed,
      isR18Hidden,
    });
    const maskKind = getCatalogImageMaskKind({
      isNg,
      ngRevealed,
      isR18Hidden,
    });

    // 既読判定
    const isViewed = viewedItem !== undefined;
    const isExpired = isThreadExpired(thread);

    // スレメニュー表示方法を判別する
    const [longPressOpensMenu, isMenuButtonDisplayed] = useMemo(() => {
      if (threadMenuOpenMethod === "auto") {
        // 「自動」の場合は自動判別を行う
        if (
          typeof window !== "undefined" &&
          window.matchMedia("(pointer: fine)").matches
        ) {
          // マウスが存在する環境の場合(PC)はメニューボタンへ判別する
          return [false, true];
        } else {
          // それ以外の場合は長押しへ判別する。
          // これは何らかの理由によりwindowが利用できない場合を含む(当設定が追加される前の挙動を真似する)
          return [true, false];
        }
      } else if (threadMenuOpenMethod === "long-press") {
        return [true, false];
      } else if (threadMenuOpenMethod === "menu-button") {
        return [false, true];
      } else if (threadMenuOpenMethod === "off") {
        return [false, false];
      } else {
        throw new Error(
          `Unexpected value: ${threadMenuOpenMethod satisfies never}`,
        );
      }
    }, [threadMenuOpenMethod]);

    const longPressHandlers = useLongPress({
      onLongPress: (): void => setMenuOpen(true),
    });

    const handleNgClick = (e: React.MouseEvent): void => {
      if (isNg) {
        e.preventDefault();
        e.stopPropagation();
        if (isR18Hidden) pendingFocusRef.current = "r18";
        setNgRevealed(true);
      }
    };

    const handleR18Click = (e: React.MouseEvent): void => {
      if (isR18Hidden) {
        e.preventDefault();
        e.stopPropagation();
        pendingFocusRef.current = "thumbnail";
        setR18Revealed(true);
      }
    };

    const handleThreadClick = (e: React.MouseEvent): void => {
      if (getCatalogThreadClickAction({ isNg, ngRevealed }) === "toggle-ng") {
        handleNgClick(e);
        return;
      }
      addViewed(boardSlug, thread.id);
    };

    const handleTitleClick = (e: React.MouseEvent): void => {
      if (getCatalogThreadClickAction({ isNg, ngRevealed }) === "toggle-ng") {
        handleNgClick(e);
        return;
      }
      addViewed(boardSlug, thread.id);
    };

    useEffect(() => {
      if (pendingFocusRef.current === "thumbnail" && r18Revealed) {
        pendingFocusRef.current = null;
        thumbnailLinkRef.current?.focus();
      } else if (
        pendingFocusRef.current === "r18" &&
        maskKind === "r18" &&
        ngRevealed
      ) {
        pendingFocusRef.current = null;
        r18TriggerRef.current?.focus();
      }
    }, [maskKind, ngRevealed, r18Revealed]);

    const handleMenuClick = (): void => {
      setMenuOpen(true);
    };

    const unreadCount = viewedItem
      ? unreadReplyCount(viewedItem.readReplyNumber, totalCount)
      : undefined;

    const thumbnail = (
      <div
        className={clsx(
          "relative aspect-square bg-muted flex items-center justify-center catalog-configured-thumb",
          isNg &&
            ngRevealed &&
            !isR18Hidden &&
            "after:absolute after:inset-0 after:cursor-pointer after:bg-transparent after:content-['']",
        )}
        style={{
          width: "100%",
          maxHeight: `${imageSize}px`,
        }}
      >
        <img
          src={imageUrl}
          alt={accessibleTitle}
          loading="lazy"
          className={clsx(
            "w-full h-full object-contain",
            maskKind === "ng" && "blur-xl opacity-20",
            maskKind === "r18" && "r18-spoiler-image",
          )}
          onError={(e: React.SyntheticEvent<HTMLImageElement>): void => {
            (e.target as HTMLImageElement).src = noImage;
          }}
        />

        {isVideo && !maskKind && <VideoBadge />}
        {isOekaki && maskKind !== "ng" && <OekakiBadge />}
        <CatalogReplyCount
          maskKind={maskKind}
          showCount={showCount}
          showUnreadCount={showUnreadCount}
          totalCount={totalCount}
          unreadCount={unreadCount}
        />
        {showNew && isNew && !maskKind && (
          <strong className="absolute top-1 left-1 px-1.5 py-0.5 text-xs font-bold bg-primary text-primary-foreground rounded">
            NEW
          </strong>
        )}
        {maskKind === "ng" ? (
          <div
            className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-background/95"
            aria-hidden="true"
          >
            <strong className="flex items-center gap-1 px-3 py-2 bg-destructive/90 text-destructive-foreground rounded text-sm font-bold">
              <MdBlock aria-hidden="true" size={16} />
              NG
            </strong>
            <small className="text-xs text-muted-foreground">
              タップで表示
            </small>
          </div>
        ) : null}
        {isNg && ngRevealed && !isR18Hidden && (
          <strong className="absolute top-1 left-1 px-2 py-1 bg-destructive/90 text-destructive-foreground rounded text-xs font-bold flex items-center gap-1 pointer-events-none">
            <MdBlock aria-hidden="true" size={12} />
            NG
          </strong>
        )}
      </div>
    );

    return (
      <>
        <article
          ref={elementRef}
          data-catalog-layout-key={thread.id}
          aria-label={accessibleTitle}
          className={clsx(
            "group relative border border-transparent bg-card/50 rounded-lg overflow-hidden hover:bg-card transition-colors",
            isViewed && "catalog-item-viewed",
            isSuperFavorite && "catalog-item-super-favorite",
            isExpired && "catalog-item-expired",
          )}
        >
          <figure
            className="catalog-thumbnail-shell"
            style={{
              width: `min(100%, ${imageSize}px)`,
              maxHeight: `${imageSize}px`,
            }}
          >
            {maskKind === "r18" && (
              <button
                ref={r18TriggerRef}
                type="button"
                className="catalog-r18-spoiler-trigger"
                aria-label={`${accessibleTitle}のR18画像を表示`}
                onMouseEnter={(): void => setIsHovered(true)}
                onMouseLeave={(): void => setIsHovered(false)}
                onFocus={(): void => setIsHovered(true)}
                onBlur={(): void => setIsHovered(false)}
                {...(longPressOpensMenu ? longPressHandlers : {})}
                onClick={handleR18Click}
              />
            )}
            <CatalogThreadLink
              ref={thumbnailLinkRef}
              thread={thread}
              className="block"
              {...threadLinkAttributes}
              aria-label={
                maskKind === "ng"
                  ? `${accessibleTitle}のNG画像を表示`
                  : undefined
              }
              onMouseEnter={(): void => setIsHovered(true)}
              onMouseLeave={(): void => setIsHovered(false)}
              onFocus={(): void => setIsHovered(true)}
              onBlur={(): void => setIsHovered(false)}
              {...(longPressOpensMenu ? longPressHandlers : {})}
              onClick={handleThreadClick}
            >
              {thumbnail}
            </CatalogThreadLink>
          </figure>
          {(textLines > 0 || isMenuButtonDisplayed) && (
            <header
              className={clsx(
                "flex items-start px-2",
                textLines > 0 ? "pt-2 mb-2" : "pt-0 mb-0",
              )}
            >
              {textLines > 0 && (
                <CatalogThreadLink
                  thread={thread}
                  {...threadLinkAttributes}
                  aria-label={
                    maskKind === "ng"
                      ? `${accessibleTitle}のNGスレッド情報を表示`
                      : undefined
                  }
                  onMouseEnter={(): void => setIsHovered(true)}
                  onMouseLeave={(): void => setIsHovered(false)}
                  onFocus={(): void => setIsHovered(true)}
                  onBlur={(): void => setIsHovered(false)}
                  className={clsx(
                    "catalog-item-text catalog-text-clamp min-w-0 flex-1 text-xs text-muted-foreground leading-tight",
                    "group-hover:text-foreground transition-colors",
                    maskVisibility.text && "blur-sm",
                  )}
                  style={
                    {
                      "--catalog-text-lines": textLines,
                    } as React.CSSProperties
                  }
                  {...(longPressOpensMenu ? longPressHandlers : {})}
                  onClick={handleTitleClick}
                >
                  {extendedTextEffects ? (
                    <TextEffects text={displayBody} extended />
                  ) : (
                    displayBody
                  )}
                </CatalogThreadLink>
              )}
              {isMenuButtonDisplayed && (
                <button
                  type="button"
                  onClick={handleMenuClick}
                  className="flex-shrink-0 flex items-center justify-center rounded-full text-muted-foreground hover:text-foreground hover:bg-muted transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 ml-auto"
                  aria-label="スレッドメニュー"
                >
                  <HiOutlineDotsVertical className="w-5 h-5" />
                </button>
              )}
            </header>
          )}
          <footer className="catalog-item-tags-row flex items-start gap-1 px-2 pb-2">
            <TagBadges
              tags={categoryTags}
              className="catalog-tags min-w-0 flex-1"
            />
            <CatalogBoardIcon slug={thread.b} className="ml-auto" />
          </footer>
        </article>

        <ThreadContextMenu
          thread={thread}
          isOpen={menuOpen}
          onClose={(): void => setMenuOpen(false)}
        />
      </>
    );
  });
